import type {
  DownloadAppEvent,
  RegistrationEvent,
  RegistrationValuePropEvent,
  TrackDownloadAppEventFn,
  TrackRegistrationEventFn,
  TrackRegistrationValuePropEventFn,
} from "./analytics";
import type { RegistrationConfig } from "../../providers/RegistrationProvider";

export interface RegistrationRuntime {
  config: RegistrationConfig;
  trackRegistrationEvent: TrackRegistrationEventFn;
  trackRegistrationValuePropEvent: TrackRegistrationValuePropEventFn;
  trackDownloadAppEvent: TrackDownloadAppEventFn;
  buildImageUrl: (relativePath: string) => string;
  setApiBaseUrl: (url: string) => void;
  getApiBaseUrl: () => string | undefined;
}

let runtime: RegistrationRuntime | null = null;

const RUNTIME_GLOBAL_KEY = "__FMC_REGISTRATION_RUNTIME__";

type GlobalWithRuntime = typeof globalThis & {
  [RUNTIME_GLOBAL_KEY]?: RegistrationRuntime;
};

function getSharedRuntime(): RegistrationRuntime | null {
  const globalRuntime = (globalThis as GlobalWithRuntime)[RUNTIME_GLOBAL_KEY];
  return globalRuntime ?? runtime;
}

function setSharedRuntime(value: RegistrationRuntime | null): void {
  runtime = value;
  if (value) {
    (globalThis as GlobalWithRuntime)[RUNTIME_GLOBAL_KEY] = value;
  } else {
    delete (globalThis as GlobalWithRuntime)[RUNTIME_GLOBAL_KEY];
  }
}

export function setRegistrationRuntime(value: RegistrationRuntime): void {
  setSharedRuntime(value);
}

export function clearRegistrationRuntime(): void {
  setSharedRuntime(null);
}

/** @internal Clears module-local copy only — simulates duplicate Vite module instances in tests */
export function clearModuleLocalRuntimeForTests(): void {
  runtime = null;
}

export function getRegistrationRuntime(): RegistrationRuntime {
  const shared = getSharedRuntime();
  if (!shared) {
    throw new Error("Registration runtime not initialized. Wrap routes with RegistrationProvider.");
  }
  return shared;
}

export function trackRegistrationEvent(
  event: RegistrationEvent,
  attributes?: Record<string, unknown>,
): void {
  getRegistrationRuntime().trackRegistrationEvent(event, attributes);
}

export function trackRegistrationValuePropEvent(
  event: RegistrationValuePropEvent,
  attributes?: Record<string, unknown>,
): void {
  getRegistrationRuntime().trackRegistrationValuePropEvent(event, attributes);
}

export function trackDownloadAppEvent(
  event: DownloadAppEvent,
  attributes?: Record<string, unknown>,
): void {
  getRegistrationRuntime().trackDownloadAppEvent(event, attributes);
}

export function buildImageUrl(relativePath: string): string {
  return getRegistrationRuntime().buildImageUrl(relativePath);
}

/** Config shim matching auth-shell config.env() pattern */
export const config = {
  env: (key: string): string => {
    const cfg = getRegistrationRuntime().config;
    const map: Record<string, keyof RegistrationConfig | undefined> = {
      registrationNpsSurveyId: "registrationNpsSurveyId",
      qualtricsUrl: "qualtricsUrl",
      qualtricsAPIKey: "qualtricsAPIKey",
      cdnRootUrl: "cdnRootUrl",
      mobileAppDownloadUrl: "mobileAppDownloadUrl",
      baseUrl: "baseUrl",
    };
    const mapped = map[key];
    if (mapped) {
      const val = cfg[mapped];
      return val == null ? "" : String(val);
    }
    return "";
  },
};

export async function loadConfig(): Promise<{ api: { baseUrl: string; baseUrlV2?: string } }> {
  const cfg = getRegistrationRuntime().config;
  return {
    api: {
      baseUrl: cfg.apiBaseUrl,
      baseUrlV2: cfg.apiBaseUrlV2,
    },
  };
}

export {
  RegistrationEvent,
  RegistrationValuePropEvent,
  DownloadAppEvent,
} from "./analytics";
